/**
 * Canonical copy and structure for the public landing page.
 *
 * What: Central source of truth for landing section ids and brand-voice copy.
 * Why: Keeps section components presentational while messaging stays consistent and easy to iterate.
 * When: Update when changing landing messaging, section order/ids, or introducing new audiences/CTAs.
 *
 * The rendered structure follows the approved full-page mockup
 * (`docs/designs/landing-page-2026-07-14/variant-A-full-page.html`, decisions
 * D1-A / D2-A in that folder's README): hero + product card, trust strip with
 * the university-logo marquee, proof band, who-teaches, students & parents,
 * pricing, explore-by-level, CTA band, FAQ. The persona data in
 * `heroProductCard` and `proof` ships exactly as designed (D1-A). Copy voice:
 * state facts and the benefit to the child — never coach comparisons against
 * other programmes, cite our own costs, or dwell on avoided bad outcomes.
 */

import {
  CLASS_SIZE_BULLET,
  CLASS_SIZE_FAQ,
  CLASS_SIZE_PRICING_NOTE,
  MAX_CLASS_SIZE,
} from '@/content/classSizeContent';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface LandingContent {
  brandName: string;
  seo: { title: string; description: string };
  nav: typeof landingNavConfig;
  hero: {
    eyebrow: string;
    headline: string;
    /** Substring of `headline` rendered in the brand gradient. */
    headlineHighlight: string;
    subhead: string;
    primaryCtaLabel: string;
    secondaryCtaLabel: string;
    microLine: string;
    /**
     * Level chips, grouped by what must wrap together. Each inner array is one
     * unbreakable run: the row wraps between groups, never inside one.
     */
    levelChips: string[][];
  };
  /**
   * Persona data for the hero product card (Sarah's mastery map + Ms Tan's
   * class card). Ships as designed per D1-A — no "illustrative" caption.
   */
  heroProductCard: {
    urlLabel: string;
    avatarInitial: string;
    mapTitle: string;
    mapSubtitle: string;
    masteryStat: string;
    masteryStatLabel: string;
    tutorInitials: string;
    tutorName: string;
    tutorSession: string;
    seatInitials: string[];
    openSeatLabel: string;
    focusLabel: string;
    focusTopic: string;
    streakLine: string;
    practiseCtaLabel: string;
    masteredBadgeTitle: string;
    masteredBadgeTopic: string;
  };
  /** Trust strip under the hero — the page's one textual "capped at 4". */
  trust: {
    items: Array<{
      pre?: string;
      /** Emphasised fragment rendered in brand orange. */
      highlight?: string;
      post?: string;
      detail: string;
    }>;
  };
  whoTeaches: {
    eyebrow: string;
    title: string;
    body: string;
    universityLogosLabel: string;
    universityLogos: Array<{
      shortName: string;
      name: string;
      logoSrc: string;
    }>;
    screeningTitle: string;
    screeningBullets: string[];
    consistencyTitle: string;
    consistencyBody: string;
    tuitionTitle: string;
    tuitionBody: string;
  };
  /** Proof band: before→after arc + the weekly loop. */
  proof: {
    eyebrow: string;
    title: string;
    beforeAfter: {
      capLabel: string;
      deltaLabel: string;
      topicLine: string;
      before: { label: string; pct: number };
      reteach: { label: string; chipLabel: string };
      after: { label: string; pct: number };
      mapLabel: string;
      mapSummary: string;
    };
    loop: {
      capLabel: string;
      repeatLabel: string;
      steps: Array<{ title: string; description: string }>;
    };
  };
  /** Merged students & parents section. */
  audience: {
    eyebrow: string;
    title: string;
    students: { label: string; kicker: string; bullets: string[] };
    parents: { label: string; kicker: string; bullets: string[] };
  };
  faqItems: FaqItem[];
  relatedLinks: {
    eyebrow: string;
    title: string;
    body: string;
    links: Array<{ label: string; description: string; path: string }>;
  };
  pricing: {
    eyebrow: string;
    title: string;
    body: string;
    /** Grouped like `hero.levelChips` — the row wraps between groups only. */
    coverageChips: string[][];
    feeLabel: string;
    price: string;
    priceUnit: string;
    priceDetails: string[];
    priceNote: string;
    feeCollectionNote: string;
    trialBadge: string;
    trialTitle: string;
    trialBody: string;
    trialLead: string;
    trialBullets: string[];
    trialCtaLabel: string;
  };
  whyWeBuilt: {
    eyebrow: string;
    title: string;
    subtitle: string;
    body: string;
    intentionalTitle: string;
    intentionalBody: string;
    intentionalBullets: string[];
    closingLine: string;
  };
  approach: {
    eyebrow: string;
    title: string;
    body: string;
    systemLabel: string;
    parts: Array<{ number: string; title: string; description: string }>;
    loopLines: string[];
  };
  cta: {
    title: string;
    body: string;
    primaryCtaLabel: string;
    secondaryCtaLabel: string;
    footerLine: string;
  };
  socialMission: {
    eyebrow: string;
    title: string;
    body: string;
    footerNote: string;
  };
}

export const landingSectionIds = {
  hero: 'hero',
  trust: 'trust',
  proof: 'proof',
  whoTeaches: 'who-teaches',
  audience: 'audience',
  /** Card-level anchor inside the audience section (footer "For students"). */
  experience: 'experience',
  /** Card-level anchor inside the audience section (footer "For parents"). */
  tracking: 'tracking',
  pricing: 'pricing',
  relatedLinks: 'related-links',
  cta: 'cta',
  faq: 'faq',
};

export const landingNavItemTypes = Object.freeze({
  section: 'section' as const,
  route: 'route' as const,
  dropdown: 'dropdown' as const,
});

/**
 * Canonical navigation configuration for the landing page.
 *
 * What: Defines typed desktop/mobile nav items for landing sections, route links, and dropdown menus.
 * Why: Ensures navigation semantics are explicit so shared consumers can render section jumps and routes correctly.
 * When: Update whenever section ids, top-level routes, or dropdown group composition changes.
 */
export const landingNavConfig = {
  desktop: [
    {
      type: landingNavItemTypes.section,
      id: landingSectionIds.whoTeaches,
      label: 'Our Teachers',
    },
    {
      type: landingNavItemTypes.section,
      id: landingSectionIds.pricing,
      label: 'Pricing',
    },
    {
      type: landingNavItemTypes.dropdown,
      label: 'Levels',
      isDropdown: true,
      isMegaMenu: true,
      groups: [
        {
          title: 'Subjects',
          description: 'Choose your subject',
          items: [
            { label: 'Mathematics', path: '/secondary-school' },
            { label: 'Secondary English', path: '/english' },
            { label: 'Secondary Chemistry', path: '/chemistry' },
          ],
        },
        {
          title: 'Primary Levels',
          description: 'Primary 1–6',
          items: [
            { label: 'Primary 1', path: '/primary-1', isHub: true },
            { label: 'Primary 2', path: '/primary-2', isHub: true },
            { label: 'Primary 3', path: '/primary-3', isHub: true },
            { label: 'Primary 4', path: '/primary-4', isHub: true },
            { label: 'Primary 5', path: '/primary-5', isHub: true },
            { label: 'Primary 6', path: '/primary-6', isHub: true },
          ],
        },
        {
          title: 'Secondary Levels',
          description: 'G1 / G2 / G3 / IP',
          items: [
            { label: 'Secondary 1', path: '/sec-1', isHub: true },
            { label: 'Secondary 2', path: '/sec-2', isHub: true },
            { label: 'Secondary 3', path: '/sec-3', isHub: true },
            { label: 'Secondary 4', path: '/sec-4', isHub: true },
          ],
        },
        {
          title: 'Exam Preparation',
          description: 'PSLE, O-Level / SEC, N-Level, IP, IB, E-Math & A-Math',
          items: [
            { label: 'PSLE', path: '/psle' },
            { label: 'O-Level / SEC', path: '/o-level' },
            { label: 'N-Level', path: '/n-level' },
            { label: 'E-Math', path: '/e-math' },
            { label: 'A-Math', path: '/a-math' },
            { label: 'IP Programme', path: '/integrated-programme' },
            { label: 'IB Mathematics', path: '/ib' },
          ],
        },
      ],
    },
    {
      type: landingNavItemTypes.route,
      path: '/about-us',
      label: 'About Us',
    },
    {
      type: landingNavItemTypes.route,
      path: '/blog',
      label: 'Blog',
    },
  ],
  mobile: [
    {
      type: landingNavItemTypes.section,
      id: landingSectionIds.whoTeaches,
      label: 'Our Teachers',
      iconKey: 'whoTeaches',
    },
    {
      type: landingNavItemTypes.section,
      id: landingSectionIds.pricing,
      label: 'Pricing',
      iconKey: 'pricing',
    },
    {
      type: landingNavItemTypes.dropdown,
      label: 'Levels',
      iconKey: 'subjects',
      isDropdown: true,
      groups: [
        {
          title: 'Subjects',
          description: 'Choose your subject',
          items: [
            { label: 'Mathematics', path: '/secondary-school' },
            { label: 'Secondary English', path: '/english' },
            { label: 'Secondary Chemistry', path: '/chemistry' },
          ],
        },
        {
          title: 'Primary Levels',
          items: [
            { label: 'Primary 1', path: '/primary-1' },
            { label: 'Primary 2', path: '/primary-2' },
            { label: 'Primary 3', path: '/primary-3' },
            { label: 'Primary 4', path: '/primary-4' },
            { label: 'Primary 5', path: '/primary-5' },
            { label: 'Primary 6', path: '/primary-6' },
          ],
        },
        {
          title: 'Secondary Levels',
          description: 'G1 / G2 / G3 / IP',
          items: [
            { label: 'Secondary 1', path: '/sec-1' },
            { label: 'Secondary 2', path: '/sec-2' },
            { label: 'Secondary 3', path: '/sec-3' },
            { label: 'Secondary 4', path: '/sec-4' },
          ],
        },
        {
          title: 'Exam Prep',
          items: [
            { label: 'PSLE', path: '/psle' },
            { label: 'O-Level / SEC', path: '/o-level' },
            { label: 'N-Level', path: '/n-level' },
            { label: 'E-Math', path: '/e-math' },
            { label: 'A-Math', path: '/a-math' },
            { label: 'IP Programme', path: '/integrated-programme' },
            { label: 'IB Mathematics', path: '/ib' },
          ],
        },
      ],
    },
    {
      type: landingNavItemTypes.route,
      path: '/about-us',
      label: 'About Us',
      iconKey: 'aboutUs',
    },
    {
      type: landingNavItemTypes.route,
      path: '/blog',
      label: 'Blog',
      iconKey: 'blog',
    },
  ],
};

/**
 * Shared "Explore" footer links rendered across landing-shell pages.
 *
 * What: Defines route-aware section links and static route links for the footer Explore column.
 * Why: Keeps footer and top-level navigation labels/targets aligned without duplicated hardcoded links.
 * When: Update whenever Explore labels or destinations should change.
 */
export const landingFooterExploreLinks = [
  {
    type: landingNavItemTypes.route,
    path: '/about-us',
    label: 'About Us',
  },
  {
    type: landingNavItemTypes.section,
    id: landingSectionIds.pricing,
    label: 'Pricing',
  },
  {
    type: landingNavItemTypes.section,
    id: landingSectionIds.experience,
    label: 'For students',
  },
  {
    type: landingNavItemTypes.section,
    id: landingSectionIds.tracking,
    label: 'For parents',
  },
  {
    type: landingNavItemTypes.route,
    path: '/blog',
    label: 'Blog',
  },
];

export const buildLandingContent = ({
  brandName = 'DeepThink',
}: { brandName?: string } = {}): LandingContent => {
  return {
    brandName,
    seo: {
      title: `Singapore Math Tuition for Primary and Secondary Students | ${brandName}`,
      description:
        'Singapore math tuition combining weekly live online classes with targeted practice and instant feedback. Students find the topics costing marks and fix them properly.',
    },
    nav: landingNavConfig,
    hero: {
      eyebrow: 'Singapore Math Tuition · Primary 1 to Sec 4',
      headline:
        'See exactly which topics are costing marks, then fix them properly.',
      headlineHighlight: 'costing marks',
      subhead: `Adaptive practice pinpoints your child’s weak spots — so our tutors walk into every live class knowing exactly what to fix.`,
      primaryCtaLabel: 'Book a Free Trial',
      secondaryCtaLabel: 'Try the Practice Demo',
      microLine: `Free · no commitment · we’ll WhatsApp you within 24 hours`,
      levelChips: [
        ['PSLE'],
        ['O-Level / N-Level / SEC'],
        ['IP'],
        ['IB'],
        // The two subject chips wrap as a pair — A-Math stranded alone on a
        // row of its own reads as a layout accident on phones.
        ['E-Math', 'A-Math'],
      ],
    },
    heroProductCard: {
      urlLabel: 'deepthink.sg/practice',
      avatarInitial: 'S',
      mapTitle: `Sarah’s Mastery Map`,
      mapSubtitle: 'Secondary 2 · Math',
      masteryStat: '78%',
      masteryStatLabel: 'mastered',
      tutorInitials: 'MT',
      tutorName: `Ms Tan · ${brandName} tutor`,
      tutorSession: 'Live class · Thu 7:30pm',
      seatInitials: ['S', 'J', 'M'],
      openSeatLabel: '+1',
      focusLabel: 'Focus',
      focusTopic: 'Quadratics',
      streakLine: '12-day streak',
      practiseCtaLabel: 'Practise this →',
      masteredBadgeTitle: 'Node mastered',
      masteredBadgeTopic: 'Linear equations',
    },
    trust: {
      items: [
        {
          highlight: 'S$50',
          post: ' per live class',
          detail: '1.5-hour weekly live class · platform included',
        },
        {
          pre: 'Live classes capped at ',
          highlight: String(MAX_CLASS_SIZE),
          detail: 'grouped by pace — if no class fits, we open a new one',
        },
        {
          pre: 'Free trial, no commitment',
          detail: `We’ll WhatsApp you within 24 hours`,
        },
      ],
    },
    whoTeaches: {
      eyebrow: 'WHO TEACHES AT DEEPTHINK',
      title:
        'Teachers who can diagnose gaps, teach clearly, and move students forward.',
      body: 'DeepThink tutors come from strong academic backgrounds and are selected for clear explanation, structured teaching, and the ability to focus on the topics that matter most.',
      universityLogosLabel: 'Tutor backgrounds include',
      universityLogos: [
        { shortName: 'UCLA', name: 'UCLA', logoSrc: '/logos/UCLA.svg' },
        {
          shortName: 'NUS',
          name: 'National University of Singapore',
          logoSrc: '/logos/NUS.svg',
        },
        {
          shortName: 'NTU',
          name: 'Nanyang Technological University',
          logoSrc: '/logos/NTU.svg',
        },
        { shortName: 'SMU', name: 'SMU Singapore', logoSrc: '/logos/SMU.svg' },
        { shortName: 'INSEAD', name: 'INSEAD', logoSrc: '/logos/INSEAD.svg' },
        {
          shortName: 'CMU',
          name: 'Carnegie Mellon University',
          logoSrc: '/logos/cmu.svg',
        },
      ],
      screeningTitle: 'We look for teachers who can:',
      screeningBullets: [
        'Diagnose accurately — identify weak topics and recurring error patterns with precision',
        'Explain clearly — break difficult ideas into steps students can actually work with',
        'Teach with structure — build understanding progressively instead of rushing through content',
        'Respond in real time — adjust when confusion appears, not after it compounds',
      ],
      consistencyTitle: 'A shared instructional framework across classes',
      consistencyBody:
        'DeepThink lessons follow aligned materials and a consistent teaching approach. Tutors review common error patterns, focus class time on the topics that need the most attention, and connect lessons to the practice students complete outside class.\n\nThat keeps teaching, reinforcement, and progress aligned week after week.',
      tuitionTitle: 'Why families switch to DeepThink',
      tuitionBody:
        'Most students do not need more math work. They need the right work at the right time.\n\nDeepThink is built to reduce wasted effort, lower overwhelm, and turn each week into a clearer next step.',
    },
    proof: {
      eyebrow: 'Why it works',
      title: 'Spotted in practice. Fixed in class.',
      beforeAfter: {
        capLabel: 'Algebraic manipulation',
        deltaLabel: 'Weak → Mastered',
        topicLine: 'Flagged in practice, retaught in class, confirmed fixed',
        before: { label: 'Before', pct: 34 },
        reteach: {
          label: 'In class',
          chipLabel: `Retaught live by ${brandName} Teacher`,
        },
        after: { label: 'After', pct: 92 },
        mapLabel: 'Sec 2 mastery map',
        mapSummary: '4 of 6 mastered',
      },
      loop: {
        capLabel: 'How a week works',
        repeatLabel: 'Repeats weekly',
        steps: [
          {
            title: 'Practice finds the gap',
            description: `Adaptive questions pinpoint what’s costing marks`,
          },
          {
            title: 'A real tutor reteaches it, live',
            description: 'In a small group, matched by pace',
          },
          {
            title: 'Practice confirms mastery',
            description: `…and lines up what’s next for class time`,
          },
        ],
      },
    },
    audience: {
      eyebrow: 'For students and parents',
      title: 'Students get clarity. Parents get visibility.',
      students: {
        label: 'For students',
        kicker: 'Clarity, not more random homework',
        bullets: [
          'Know which topics need work instead of revising everything at once',
          'See mistakes earlier and correct them while the thinking is still fresh',
          'Build confidence by fixing weak topics properly and watching the mastery map fill in',
        ],
      },
      parents: {
        label: 'For parents',
        kicker: 'Visibility, not vague reassurance',
        bullets: [
          'See which topics are improving and which still need reinforcement',
          'Know class time and practice are pointed at the right areas each week',
          'Follow progress without chasing for updates after every lesson',
        ],
      },
    },
    faqItems: [
      {
        question: 'Do you offer English and Chemistry classes?',
        answer:
          'Yes. Class registration for the 2027 school year is open. Secondary English covers G3, IP and IB English in Sec 1–4. Sec 3–4 Chemistry covers Pure Chemistry and Combined Science (Chemistry) at G3 and G2. Lessons are live online, capped at four students and run for 1.5 hours weekly, with subject practice included. Request a trial class through the English or Chemistry page; we will message you to confirm the 2027 level.',
      },
      {
        question: 'How much does math tuition cost in Singapore?',
        answer: `Group math tuition in Singapore typically costs S$50–S$95 per session depending on level, class size, and centre. ${brandName} charges S$50 per 1.5-hour live class, with the same fee across all levels and streams. That single fee covers the full programme: a live class of at most four students, plus the practice platform between classes.`,
      },
      CLASS_SIZE_FAQ,
      {
        question: 'Is online math tuition as effective as in-person?',
        answer:
          'Yes, when teaching and practice are connected. Online tuition removes travel time and lets students review materials at their own pace. The key is whether the programme identifies weak topics and follows up on them — not the delivery format.',
      },
      {
        question: 'When should my child start math tuition?',
        answer:
          'The best time is when you notice recurring mistakes or falling confidence — not when grades have already dropped significantly. Early intervention means less catching up later. We support students from Primary 1 through Secondary 4.',
      },
      {
        question: 'What makes DeepThink different from regular math tuition?',
        answer: `Two things. First, ${brandName} connects teaching and practice in a weekly loop: practice identifies weak topics, tutors address them in class, and students reinforce them with feedback afterwards. Second, every class has at most ${MAX_CLASS_SIZE} students, grouped by learning pace, so lessons are pitched at the students in the room. If no session matches a child’s pace, we open a new one.`,
      },
      {
        question: 'Which levels and streams do you support?',
        answer:
          'Primary 1 to Primary 6, Secondary 1 to Secondary 4, and all MOE streams — including G1, G2, G3, PSLE, O-Level, N-Level, IP, E-Math, and A-Math. We also fully support students preparing for the Singapore-Cambridge SEC (replacing O-Levels, N(A)-Levels, and N(T)-Levels from 2027).',
      },
      {
        question: 'How does the weekly class work?',
        answer:
          'Students attend a 1.5-hour live online class each week, then continue with targeted practice that reveals weak topics, provides instant marking, and reinforces what was taught.',
      },
      {
        question: 'Can families try a class before committing?',
        answer: `Yes. Families can book a free trial class to experience the teaching pace, class structure, and how ${brandName} diagnoses and reinforces weak topics.`,
      },
      {
        question: "How do parents track their child's progress?",
        answer:
          'Parents can see which topics their child is working on, which areas are improving, and where extra reinforcement is still needed — without having to chase for updates after every lesson.',
      },
    ],
    relatedLinks: {
      eyebrow: 'Explore by Level',
      title: 'Find your starting point',
      body: 'Students — jump straight to your level. Parents — find the right support for where your child is today.',
      links: [
        {
          label: 'Secondary English',
          path: '/english',
          description:
            'G3, IP and IB English for Sec 1–4. Class registration for the 2027 school year is open.',
        },
        {
          label: 'Secondary Chemistry',
          path: '/chemistry',
          description:
            'Pure Chemistry and Combined Science (Chemistry) for Sec 3–4. Request a trial class for 2027.',
        },
        {
          label: 'Primary School',
          description:
            'Structured support for Primary 1–6, including PSLE preparation and targeted topic reinforcement.',
          path: '/primary-school',
        },
        {
          label: 'Secondary School',
          description:
            'Coverage across Secondary 1–4, with support for different streams and exam demands.',
          path: '/secondary-school',
        },
        {
          label: 'Blog',
          description:
            'Revision strategies, study advice, and math learning resources for students and parents.',
          path: '/blog',
        },
      ],
    },
    pricing: {
      eyebrow: 'Pricing',
      title: 'Clear pricing. One consistent fee.',
      body: 'S$50 per live class across Primary 1–6 and Secondary 1–4 — every stream, platform included. No hidden add-ons.',
      coverageChips: [
        ['PSLE'],
        ['G1 / G2 / G3'],
        ['O-Level / SEC'],
        ['N-Level'],
        ['IP'],
        ['IB'],
        ['E-Math', 'A-Math'],
      ],
      feeLabel: 'Weekly Fee',
      price: 'S$50',
      priceUnit: 'per live class',
      priceDetails: [
        '1.5 hours of weekly online math tuition',
        CLASS_SIZE_BULLET,
        'Access to the DeepThink practice platform included',
      ],
      priceNote: CLASS_SIZE_PRICING_NOTE,
      feeCollectionNote: 'Fees are collected every 4 classes.',
      trialBadge: 'Start Here',
      trialTitle: 'Start with a free trial class',
      trialBody:
        'See how DeepThink teaches, diagnoses, and reinforces topics before deciding if it is the right fit.',
      trialLead: 'It provides a clear sense of:',
      trialBullets: [
        'Teaching pace and class structure',
        'How weak topics are explained and corrected',
        'How practice and feedback continue after class',
      ],
      trialCtaLabel: 'Book a Free Trial',
    },
    whyWeBuilt: {
      eyebrow: 'Why We Built DeepThink',
      title: 'We built DeepThink to reduce wasted effort.',
      subtitle:
        'We saw capable students spending time, attending tuition, and still missing the same topics because the next step was not clear.',
      body: 'The issue was rarely effort alone.\n\nIt was broad practice, slow feedback, and teaching that could not always target the exact weakness in time.',
      intentionalTitle: 'A tuition model built around directed effort',
      intentionalBody:
        'DeepThink was designed to make each week more precise for students, parents, and tutors.',
      intentionalBullets: [
        'Weak topics are identified instead of guessed',
        'Teaching decisions are guided by performance signals, not broad assumptions',
        'Progress is easier to see, explain, and sustain over time',
      ],
      closingLine:
        'The goal is simple: do less wasted work, focus on the right topics, and make improvement easier to repeat.',
    },
    approach: {
      eyebrow: 'THE DEEPTHINK APPROACH',
      title: 'The math system that eliminates wasted effort',
      body: 'DeepThink links live online math tuition with targeted practice so students do not have to choose between teaching and repetition. They get both, connected by feedback.\n\nInstead of more random worksheets, students get a clear next step, parents get visibility, and tutors get a stronger signal on what to fix next.',
      systemLabel: 'THE DEEPTHINK LOOP',
      parts: [
        {
          number: '1',
          title: 'Live weekly tuition that targets what matters most',
          description:
            'Tutors explain concepts, unpack common error patterns, and focus class time on the topics most likely to improve marks.',
        },
        {
          number: '2',
          title: 'Targeted practice with instant marking and solutions',
          description:
            'Students reinforce the same topics through exam-aligned practice, see mistakes immediately, and keep working until the method sticks.',
        },
      ],
      loopLines: [
        'Practice shows the weak spot.',
        'Class fixes the misunderstanding.',
        'Feedback reinforces the improvement.',
      ],
    },
    cta: {
      title: 'Math tuition works better when students know what to fix next.',
      body: `${brandName} helps students focus on the right topics, helps tutors teach with better signals, and helps parents see progress more clearly.`,
      primaryCtaLabel: 'Book a Free Trial',
      secondaryCtaLabel: 'Try the Practice Demo',
      footerLine: 'No commitment required.',
    },
    socialMission: {
      eyebrow: 'Our broader mission',
      title: 'Built for impact beyond the classroom',
      body: `${brandName} was built to help students make progress through clearer teaching, targeted practice, and better feedback.\n\nWe also believe high-quality academic support should reach further than families who can easily access it. That is why a portion of ${brandName}'s revenue is used to support students from underserved backgrounds through partner charities and educational support initiatives.\n\nOur goal is simple: build a strong learning system for our families, and use that strength to widen access over time.`,
      footerNote:
        'Part of our work supports students from underserved backgrounds.',
    },
  };
};
